import type { AxisInterval } from 'recharts/types/util/types'
import type { DataPoint, LineSeries } from './types'

import { CustomizedDot } from './styles'

/* Calculates decimal precision based on data range */
export const calculateDigits = (
  series: LineSeries[] | undefined,
  data: DataPoint[],
  hasMultipleSeries: boolean,
): number => {
  let yValues: number[] = []

  if (hasMultipleSeries) {
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
    yValues = series!.flatMap((s) =>
      (s.data || data)
        .map((point) => Number(point[s.dataKey] || point.y))
        // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
        .filter((y) => !isNaN(y)),
    )
  } else {
    // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
    yValues = data.map((point) => Number(point.y)).filter((y) => !isNaN(y))
  }

  const minY = Math.min(...yValues)
  const maxY = Math.max(...yValues)
  const difference = Math.abs(maxY - minY)

  if (difference < 2) return 2
  if (difference < 4) return 1
  return 0
}

/* Checks if chart has no data to display */
export const checkHasNoData = (
  series: LineSeries[] | undefined,
  data: DataPoint[],
  hasMultipleSeries: boolean,
): boolean => {
  const isDataEmpty = data && data.length === 0

  if (hasMultipleSeries) {
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
    return series!.every((s) => {
      const seriesData = s.data || data
      return !seriesData || seriesData.length === 0
    })
  }
  return isDataEmpty
}

/* Calculates X-axis tick interval to ensure no more than 8 ticks are displayed */
export const calculateXAxisInterval = (
  interval: AxisInterval,
  dataLength: number,
): AxisInterval => {
  if (typeof interval === 'number') {
    return Math.max(interval, Math.floor(dataLength / 8))
  }
  return interval
}

/* Normalizes series data to handle both single and multiple series configurations */
export const getLineSeries = (
  series: LineSeries[] | undefined,
  data: DataPoint[],
): LineSeries[] => {
  if (series && series.length > 0) {
    return series
  }

  return [
    {
      name: 'default',
      dataKey: 'y',
      stroke: 'var(--line-chart-stroke)',
      data: data,
    },
  ]
}

/* Returns activeDot configuration for line chart hover state */
export const getActiveDotProps = (stroke: string) => ({
  r: 3.5,
  strokeWidth: 0,
  fill: stroke,
})

/* Returns dot configuration based on animation and visibility settings */
export const getDotProps = (
  useFastAnimation: boolean,
  showDots: boolean,
  lineData: DataPoint[] | undefined,
  fallbackData: DataPoint[],
  stroke: string,
) => {
  if (useFastAnimation && !showDots) {
    return <CustomizedDot data={lineData || fallbackData} />
  }

  if (showDots) {
    return {
      r: 2.5,
      strokeWidth: 0,
      fill: stroke,
    }
  }

  return false
}
